import { FC, useCallback, useMemo, useState } from 'react'
import { Box, Text, useToast, VStack } from '@chakra-ui/react'
import useApi from '../../../common/utils/api/useApi'
import { useHasPermission } from '../../../hooks/useHasPermission'
import { useHasEntitlement } from '../../../pages/OrganizationProfilePage/planEntitlements'
import { Permission } from '../../../types/permission'
import { FeatureKey } from '../../../types/organization-entitlements'
import { DataReportsClient, DataReportsExportError } from '../DataReportsSlice'
import { EXPORT_SECTIONS } from '../constants/exportSections'
import { ExportRow } from './ExportRow'
import { ExportId } from '../types'
import { getDefaultYyyymm, isValidExportYyyymm } from '../utils/exportMonth'

interface ExportsPanelProps {
  onExportComplete: (filename: string) => void
  canExportData?: boolean
}

interface ExportHandler {
  trigger: (yyyymm: string) => Promise<unknown>
  loading: boolean
}

export const ExportsPanel: FC<ExportsPanelProps> = ({
  onExportComplete,
  canExportData: canExportDataProp,
}) => {
  const toast = useToast()
  const hasDataExportManagement = useHasPermission(Permission.DATA_EXPORT_MANAGEMENT, {
    includeOwnerSuperAdminFallback: true,
  })
  const hasDataExportEntitlement = useHasEntitlement(FeatureKey.DATA_EXPORTS)
  const defaultMonth = useMemo(() => getDefaultYyyymm(), [])

  const canExportData = canExportDataProp ?? (hasDataExportManagement && hasDataExportEntitlement)

  const showErrorToast = useCallback(
    (title: string) => {
      toast({
        title,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      })
    },
    [toast]
  )

  const handleExportSuccess = useCallback(
    (filename: string) => onExportComplete(filename),
    [onExportComplete]
  )

  const handleExportFailure = useCallback(
    (error: unknown) => {
      showErrorToast(
        error instanceof DataReportsExportError
          ? error.message
          : 'Failed to export data. Please try again.'
      )
    },
    [showErrorToast]
  )

  const useApiOptions = useMemo(
    () => ({ onSuccess: handleExportSuccess, onFailure: handleExportFailure }),
    [handleExportSuccess, handleExportFailure]
  )

  const { trigger: downloadPrescribersExport, loading: isPrescribersDownloading } = useApi(
    DataReportsClient.downloadPrescribersExportCsv,
    useApiOptions
  )

  const { trigger: downloadOrganizationAdminsExport, loading: isOrgAdminsDownloading } = useApi(
    DataReportsClient.downloadOrganizationAdminsExportCsv,
    useApiOptions
  )

  const {
    trigger: downloadPrescriberPrescriptionCountsExport,
    loading: isPrescriptionCountsDownloading,
  } = useApi(DataReportsClient.downloadPrescriberPrescriptionCountsExportCsv, useApiOptions)

  const { trigger: downloadPrescriptionsExport, loading: isPrescriptionsDownloading } = useApi(
    DataReportsClient.downloadPrescriptionsExportCsv,
    useApiOptions
  )

  const { trigger: downloadPrescribedItemsExport, loading: isPrescribedItemsDownloading } = useApi(
    DataReportsClient.downloadPrescribedItemsExportCsv,
    useApiOptions
  )

  const { trigger: downloadLitePrescribedItemsExport, loading: isLitePrescribedItemsDownloading } =
    useApi(DataReportsClient.downloadLitePrescribedItemsExportCsv, useApiOptions)

  const exportHandlers: Record<ExportId, ExportHandler> = {
    [ExportId.ACTIVE_PRESCRIBERS]: {
      trigger: () => downloadPrescribersExport({}),
      loading: isPrescribersDownloading,
    },
    [ExportId.ACTIVE_ADMINS]: {
      trigger: () => downloadOrganizationAdminsExport({}),
      loading: isOrgAdminsDownloading,
    },
    [ExportId.PRESCRIPTIONS_BY_PRESCRIBER]: {
      trigger: (yyyymm) => downloadPrescriberPrescriptionCountsExport(yyyymm),
      loading: isPrescriptionCountsDownloading,
    },
    [ExportId.PRESCRIPTIONS]: {
      trigger: (yyyymm) => downloadPrescriptionsExport(yyyymm),
      loading: isPrescriptionsDownloading,
    },
    [ExportId.PRESCRIBED_ITEMS]: {
      trigger: (yyyymm) => downloadPrescribedItemsExport(yyyymm),
      loading: isPrescribedItemsDownloading,
    },
    [ExportId.LITE_PRESCRIBED_ITEMS]: {
      trigger: (yyyymm) => downloadLitePrescribedItemsExport(yyyymm),
      loading: isLitePrescribedItemsDownloading,
    },
  }

  const isAnyExportDownloading = Object.values(exportHandlers).some((handler) => handler.loading)

  const [monthSelections, setMonthSelections] = useState<Record<string, string>>(() =>
    EXPORT_SECTIONS.flatMap((section) => section.exports)
      .filter((exportDefinition) => exportDefinition.requiresMonth)
      .reduce<Record<string, string>>((acc, exportDefinition) => {
        acc[exportDefinition.id] = defaultMonth
        return acc
      }, {})
  )

  const handleMonthChange = (exportId: string, yyyymm: string) => {
    setMonthSelections((current) => ({ ...current, [exportId]: yyyymm }))
  }

  const handleExport = async (exportId: ExportId, requiresMonth?: boolean) => {
    if (!hasDataExportManagement) {
      showErrorToast('You do not have permission to export data.')
      return
    }

    if (!canExportData) {
      return
    }

    let yyyymm = ''
    if (requiresMonth) {
      const selected = monthSelections[exportId]

      if (!selected || !isValidExportYyyymm(selected)) {
        showErrorToast('Select a valid month.')
        return
      }

      yyyymm = selected
    }

    await exportHandlers[exportId].trigger(yyyymm)
  }

  return (
    <VStack align="stretch" spacing="28px">
      {EXPORT_SECTIONS.map((section) => (
        <Box key={section.title}>
          <Text
            fontSize="11px"
            fontWeight="bold"
            color="gray.400"
            textTransform="uppercase"
            letterSpacing="0.08em"
            mb="10px"
            pl="2px"
          >
            {section.title}
          </Text>
          <Box
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="12px"
            overflow="hidden"
          >
            {section.exports.map((exportDefinition) => (
              <ExportRow
                key={exportDefinition.id}
                exportDefinition={exportDefinition}
                selectedMonth={monthSelections[exportDefinition.id]}
                onMonthChange={(yyyymm) => handleMonthChange(exportDefinition.id, yyyymm)}
                onExport={() => handleExport(exportDefinition.id, exportDefinition.requiresMonth)}
                isLoading={exportHandlers[exportDefinition.id].loading}
                isDisabled={
                  !canExportData ||
                  (isAnyExportDownloading && !exportHandlers[exportDefinition.id].loading)
                }
              />
            ))}
          </Box>
        </Box>
      ))}
    </VStack>
  )
}
